import type { RequestDetail as Detail, Me } from "@hoffman-support/contracts";
import { type FormEvent, useEffect, useState } from "react";
import { ApiError, getRequest, replyToRequest, withdrawRequest } from "../api";
import { submitFeedback } from "../triage-api";
import { Attachments } from "./Attachments";
import { STATUS_LABELS } from "./RequestList";

const AUTHOR_LABELS: Record<Detail["messages"][number]["authorKind"], string> = {
  requester: "You",
  staff: "OP Solutions",
  system: "Portal",
  agent: "OP Solutions",
};

export function RequestDetail({ me, id }: { me: Me; id: string }) {
  const [detail, setDetail] = useState<Detail | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "missing" | "error">("loading");
  const [reply, setReply] = useState("");
  const [replyKey, setReplyKey] = useState(() => crypto.randomUUID());
  const [notice, setNotice] = useState("");
  const [feedbackComment, setFeedbackComment] = useState("");
  const [feedbackKey, setFeedbackKey] = useState(() => crypto.randomUUID());
  const params = new URLSearchParams(window.location.search);

  async function load() {
    try {
      setDetail(await getRequest(id));
      setState("ready");
    } catch (error) {
      setState(error instanceof ApiError && error.failure.status === 404 ? "missing" : "error");
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: load is defined per mount and reads only stable props
  useEffect(() => {
    void load();
  }, [id]);

  async function sendReply(event: FormEvent) {
    event.preventDefault();
    if (!reply.trim()) return;
    setNotice("Sending…");
    try {
      const result = await replyToRequest(me, replyKey, id, reply);
      setReply("");
      setReplyKey(crypto.randomUUID());
      setNotice(
        result.reopened
          ? "Your reply reopened the request."
          : result.redacted
            ? "Sent. A value that looked like a credential was removed; if it was live, change it now."
            : "Sent.",
      );
      await load();
    } catch (error) {
      setNotice(
        error instanceof ApiError
          ? error.failure.message
          : "The reply could not be sent; it is kept here so you can try again.",
      );
    }
  }

  async function feedback(outcome: "fixed" | "not_fixed") {
    setNotice("Sending…");
    try {
      const result = await submitFeedback(me, feedbackKey, id, {
        outcome,
        ...(outcome === "not_fixed" ? { comment: feedbackComment } : {}),
      });
      setFeedbackKey(crypto.randomUUID());
      setFeedbackComment("");
      setNotice(
        outcome === "fixed"
          ? "Thank you. The support team will close the request."
          : `Thank you. The request is back with the support team.${result.redacted ? " A value that looked like a credential was removed from your comment." : ""}`,
      );
      await load();
    } catch (error) {
      setNotice(
        error instanceof ApiError
          ? error.failure.message
          : "Your answer could not be sent; try again.",
      );
    }
  }

  async function withdraw() {
    if (!window.confirm("Withdraw this request? OP Solutions will stop work on it.")) return;
    try {
      await withdrawRequest(me, crypto.randomUUID(), id);
      setNotice("The request was withdrawn.");
      await load();
    } catch (error) {
      setNotice(
        error instanceof ApiError ? error.failure.message : "The request could not be withdrawn.",
      );
    }
  }

  if (state === "loading") return <p role="status">Loading…</p>;
  if (state === "missing")
    return <p role="alert">There is no request with that reference in your list.</p>;
  if (state === "error" || !detail)
    return <p role="alert">The request could not be loaded. Refresh to try again.</p>;

  const canReply = !["cancelled", "withdrawn"].includes(detail.status);
  const canWithdraw = ["new", "in_progress", "waiting_on_user", "resolved"].includes(detail.status);

  return (
    <article aria-labelledby="request-heading">
      <p>
        <a href="/">Back to your requests</a>
      </p>
      <h2 id="request-heading">
        {detail.publicId}: {detail.subject}
      </h2>
      {params.get("created") ? (
        <p role="status" style={{ border: "1px solid #2a7", padding: "0.5rem" }}>
          Your request has been received. Its reference is <strong>{detail.publicId}</strong>.
          {params.get("redacted")
            ? " A value that looked like a credential was removed before saving; if it was a live credential, change it now."
            : ""}
        </p>
      ) : null}
      <dl>
        <dt>Status</dt>
        <dd>
          {STATUS_LABELS[detail.status]}
          {!detail.onBoard && detail.status !== "withdrawn" ? (
            <span style={{ marginLeft: "0.5rem", color: "#7a4b00" }}>
              (not yet on the support board)
            </span>
          ) : null}
        </dd>
        <dt>Category</dt>
        <dd>{detail.category}</dd>
        <dt>Raised</dt>
        <dd>{new Date(detail.createdAt).toLocaleString()}</dd>
        {detail.affectedSystemOrDevice ? (
          <>
            <dt>Affected system or device</dt>
            <dd>{detail.affectedSystemOrDevice}</dd>
          </>
        ) : null}
        {detail.errorText ? (
          <>
            <dt>Error text</dt>
            <dd>
              <pre style={{ whiteSpace: "pre-wrap" }}>{detail.errorText}</pre>
            </dd>
          </>
        ) : null}
        {Object.keys(detail.followUpAnswers).length > 0 ? (
          <>
            <dt>Details</dt>
            <dd>
              <ul>
                {Object.entries(detail.followUpAnswers).map(([k, v]) => (
                  <li key={k}>
                    {k}: {String(v)}
                  </li>
                ))}
              </ul>
            </dd>
          </>
        ) : null}
      </dl>
      {detail.secretRedacted ? (
        <p role="note">
          A value that looked like a credential was removed from this request before it was saved.
        </p>
      ) : null}

      <section aria-labelledby="thread-heading">
        <h3 id="thread-heading">Conversation</h3>
        <ol style={{ listStyle: "none", padding: 0 }}>
          {detail.messages.map((m) => (
            <li
              key={m.id}
              style={{
                borderLeft: "3px solid #ccc",
                padding: "0.5rem 0.75rem",
                marginBottom: "0.75rem",
              }}
            >
              <strong>{AUTHOR_LABELS[m.authorKind]}</strong>{" "}
              <span style={{ color: "#555", fontSize: "0.85rem" }}>
                {new Date(m.createdAt).toLocaleString()}
              </span>
              <p style={{ whiteSpace: "pre-wrap", margin: "0.25rem 0 0" }}>{m.body}</p>
            </li>
          ))}
        </ol>
        {canReply ? (
          <form onSubmit={sendReply}>
            <label htmlFor="reply">Reply</label>
            <br />
            <textarea
              id="reply"
              rows={4}
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              style={{ width: "100%" }}
            />
            <br />
            <button type="submit">Send reply</button>
          </form>
        ) : (
          <p>
            This request is {STATUS_LABELS[detail.status].toLowerCase()}; raise a new request if the
            problem continues.
          </p>
        )}
      </section>
      <Attachments me={me} requestId={detail.id} canUpload={canReply} />
      {detail.status === "resolved" ? (
        <section
          aria-labelledby="feedback-heading"
          style={{ border: "2px solid #2a7", padding: "0.75rem", marginBottom: "1rem" }}
        >
          <h3 id="feedback-heading">Did this resolve the issue?</h3>
          <p>
            <button type="button" onClick={() => void feedback("fixed")}>
              Yes, this is fixed
            </button>
          </p>
          <label htmlFor="feedback-comment">No, I still need help. What is still wrong?</label>
          <br />
          <textarea
            id="feedback-comment"
            rows={3}
            value={feedbackComment}
            onChange={(e) => setFeedbackComment(e.target.value)}
            style={{ width: "100%" }}
          />
          <br />
          <button
            type="button"
            onClick={() => void feedback("not_fixed")}
            disabled={feedbackComment.trim().length < 10}
          >
            No, I still need help
          </button>
        </section>
      ) : null}
      {canWithdraw ? (
        <p>
          <button type="button" onClick={() => void withdraw()}>
            Withdraw this request
          </button>
        </p>
      ) : null}
      <p role="status" aria-live="polite">
        {notice}
      </p>
    </article>
  );
}
